import { create } from 'zustand';

import { analyzeWeatherConflicts } from '@/services/weather-task-service';
import { getWeatherForProperty } from '@/services/weather-service';
import { db } from '@/services/storage/db';
import type { WeatherConflict } from '@/services/weather-task-service';

const DISMISSED_KEY = 'ridgen:dismissed-weather-conflicts';

function loadDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return new Set(parsed as string[]);
  } catch {
    // ignore parse errors
  }
  return new Set();
}

function saveDismissed(ids: Set<string>): void {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
  } catch {
    // ignore storage errors
  }
}

interface WeatherTaskState {
  conflicts: WeatherConflict[];
  isLoading: boolean;
  dismissedTaskIds: Set<string>;

  /**
   * Fetch tasks + weather for the given property and compute conflicts.
   * Filters out already-dismissed conflicts.
   */
  refreshConflicts: (propertyId: string, lat: number, lng: number) => Promise<void>;

  /** Permanently hide this conflict until the task's due date changes. */
  dismissConflict: (taskId: string) => void;

  /**
   * Accept the suggestion: update the task's due date and remove from conflicts.
   * Resets the dismissed entry for this task (new date = fresh slate).
   */
  acceptSuggestion: (taskId: string, newDate: string) => Promise<void>;
}

export const useWeatherTaskStore = create<WeatherTaskState>((set, get) => ({
  conflicts: [],
  isLoading: false,
  dismissedTaskIds: loadDismissed(),

  refreshConflicts: async (propertyId, lat, lng) => {
    set({ isLoading: true });
    try {
      const [allTasks, weather] = await Promise.all([
        db.getUpcomingTasks(50),
        getWeatherForProperty(propertyId, lat, lng),
      ]);

      if (!weather) {
        set({ isLoading: false });
        return;
      }

      const result = analyzeWeatherConflicts(allTasks, weather.daily);

      const dismissed = get().dismissedTaskIds;
      const visible = result.conflicts.filter((c) => !dismissed.has(c.taskId));

      set({ conflicts: visible, isLoading: false });
    } catch (err) {
      console.error('[weather-task-store] refreshConflicts:', err);
      set({ isLoading: false });
    }
  },

  dismissConflict: (taskId) => {
    const dismissed = new Set(get().dismissedTaskIds);
    dismissed.add(taskId);
    saveDismissed(dismissed);
    set({
      dismissedTaskIds: dismissed,
      conflicts: get().conflicts.filter((c) => c.taskId !== taskId),
    });
  },

  acceptSuggestion: async (taskId, newDate) => {
    try {
      await db.updateTask(taskId, { dueDate: newDate });
    } catch (err) {
      console.error('[weather-task-store] acceptSuggestion failed to update task:', err);
    }

    // Remove from dismissed (new date = fresh conflict check) and from visible conflicts
    const dismissed = new Set(get().dismissedTaskIds);
    dismissed.delete(taskId);
    saveDismissed(dismissed);

    set({
      dismissedTaskIds: dismissed,
      conflicts: get().conflicts.filter((c) => c.taskId !== taskId),
    });
  },
}));

/** Convenience hook — same as useWeatherTaskStore but named for clarity at callsites */
export function useWeatherTaskAlerts() {
  return useWeatherTaskStore();
}
